import { Platform } from 'src/utils/system';

enum ShortcutCategory {
  Global = 'Global',
  Conversation = 'Conversation',
  Table = 'Table',
  Inbox = 'Inbox',
}

export interface Shortcut {
  command: string;
  description: string;
  categoryName?: ShortcutCategory;
  // in general all shortcuts are disabled in modals
  // unless this flag is set to true
  enabledInModal?: boolean;
}

export const getNavigationTabCommand = (slot: number) => `ctrl+${slot}`;

export const getNavigationTabShortcut = (slot: number, tabLabel: string): Shortcut => ({
  categoryName: ShortcutCategory.Global,
  description: `Switch to ${tabLabel}`,
  command: getNavigationTabCommand(slot),
});

export const appShortcuts: Record<string, Shortcut> = {
  quickActions: {
    categoryName: ShortcutCategory.Global,
    description: 'Open Quick Actions',
    enabledInModal: true,
    command: Platform.select({
      macOS: 'command+k',
      windows: 'ctrl+k',
    }),
  },
  openNavigationTab1: getNavigationTabShortcut(1, 'Home'),
  openNavigationTab2: getNavigationTabShortcut(2, 'Settings'),
  openSearch: {
    categoryName: ShortcutCategory.Global,
    description: 'Open search',
    command: '/',
  },
  openCreateConversation: {
    categoryName: ShortcutCategory.Global,
    description: 'Open Create Conversation',
    command: 'c',
  },
  openSelectPartnerDialog: {
    categoryName: ShortcutCategory.Global,
    description: 'Switch Partner',
    command: 'p',
  },
  openMacros: {
    categoryName: ShortcutCategory.Conversation,
    description: 'Open Macros',
    command: 'm',
  },
  // bound locally by the Sidekick composer; listed here to be discoverable
  sendSidekickMessage: {
    categoryName: ShortcutCategory.Conversation,
    description: 'Send Sidekick message',
    command: Platform.select({
      macOS: 'command+enter',
      windows: 'ctrl+enter',
    }),
  },
  openSnooze: {
    categoryName: ShortcutCategory.Conversation,
    description: 'Open Snooze Dialog',
    command: 'z',
  },
  openUrgency: {
    categoryName: ShortcutCategory.Conversation,
    description: 'Change Urgency',
    command: 'u',
  },
  changeStatus: {
    categoryName: ShortcutCategory.Conversation,
    description: 'Change Status',
    command: 's',
  },
  changeAssignee: {
    categoryName: ShortcutCategory.Conversation,
    description: 'Change Assignee',
    command: 'a',
  },
  changeRequester: {
    categoryName: ShortcutCategory.Conversation,
    description: 'Change Requester',
    command: 'r',
  },
  changeType: {
    categoryName: ShortcutCategory.Conversation,
    description: 'Change Type',
    command: 't',
  },
  openConversationInNewTab: {
    categoryName: ShortcutCategory.Conversation,
    description: 'Open conversation in a new tab',
    command: Platform.select({
      macOS: 'command+click',
      windows: 'ctrl+click',
    }),
  },
  openConversationInNewBrowserTab: {
    categoryName: ShortcutCategory.Conversation,
    description: 'Open conversation in a new browser tab',
    command: 'shift+click',
  },
  selectPreviousTableRow: {
    categoryName: ShortcutCategory.Table,
    description: 'Select previous table row',
    command: 'up',
  },
  selectNextTableRow: {
    categoryName: ShortcutCategory.Table,
    description: 'Select next table row',
    command: 'down',
  },
  openSelectedTableRow: {
    categoryName: ShortcutCategory.Table,
    description: 'Open selected table row',
    command: 'enter',
  },
  inboxArchiveNotification: {
    categoryName: ShortcutCategory.Inbox,
    description: 'Archive hovered notification',
    command: 'e',
  },
  bulkActions: {
    categoryName: ShortcutCategory.Table,
    description: 'Open bulk actions',
    command: Platform.select({
      macOS: 'command+shift+k',
      windows: 'ctrl+shift+k',
    }),
  },
  selectAllVisibleTableRows: {
    categoryName: ShortcutCategory.Table,
    description: 'Select all visible rows',
    command: Platform.select({
      macOS: 'command+a',
      windows: 'ctrl+a',
    }),
  },
};
